import React from 'react';
import I18n from 'i18n-js';

import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import HeaderDropdown from '@reverbdotcom/commons/src/components/header_dropdown';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { WebThemeControl } from '@reverbdotcom/commons/src/components/web_theme_control';
import experiments from '@reverbdotcom/commons/src/experiments';
import CsrfForm from '@reverbdotcom/commons/src/csrf_form';
import { RCButton, RCText } from '@reverbdotcom/cadence/components';
import { ADMINISTRATOR_PERMISSION_LEVEL } from '@reverbdotcom/commons/src/constants';

import {
  buildSellingSection,
  buildBuyingSection,
  buildFinancesSection,
  buildMyAccountSection,
} from './reverb_header_menu_data';
import baseUrlHelpers from '../../../shared/url_helpers';
import ReverbHeaderMenuSection from './reverb_header_menu_section';
import { Core_ReverbHeaderQuery } from '@reverbdotcom/commons/src/gql/graphql';
import componentUrlHelpers from '../../shared/url_helpers';
import ReverbHeaderCreditCallouts from './reverb_header_credit_callouts';

interface IProps {
  me: Core_ReverbHeaderQuery['me'];
  activeDropdownKey: string | null;
  setActiveDropdownKey: (string) => void;
  hideMenuLabel?: boolean;
}

export const DEFAULT_AVATAR = 'https://res.cloudinary.com/reverb-preprod-cld/image/upload/s--t2fk8vm_--/v1699306207/hfgk738xixwp3yqkxjjw.jpg';

function renderSecondaryUserMenu(secondaryUserModeIsActive: boolean, managedShopName: string) {
  return (
    <>
      <div className="reverb-header__menu__user-menu__header">
        {I18n.t('discovery.reverbHeader.userMenu.secondaryUser.header')}
      </div>
      <ul>
        <li>
          <span>{managedShopName}</span>

          <CsrfForm action={baseUrlHelpers.toggleSecondaryUserModePath} method="POST">
            <RCButton
              size="mini"
              variant="transparent"
              isSubmit={true}
            >
              {secondaryUserModeIsActive ? I18n.t('discovery.reverbHeader.userMenu.secondaryUser.logOut') :
                I18n.t('discovery.reverbHeader.userMenu.secondaryUser.logIn')}
            </RCButton>
          </CsrfForm>
        </li>
      </ul>
    </>
  );
}

export function ReverbHeaderDesktopMenu({
  me,
  setActiveDropdownKey,
  activeDropdownKey,
  hideMenuLabel = false,
}: IProps) {
  const user = useUser();
  const { name, loggedOut, avatarUrl, locale, secondaryUserModeIsActive, managedShopName, managedShopUserAvatarUrl, managedShopPermission } = user;
  const { shop, reverbCredits, payoutBalances, walletBalance } = me || {};
  const { slug: shopSlug, paymentMethod: shopPaymentMethod } = shop || {};

  const isShopAdmin = !secondaryUserModeIsActive || managedShopPermission == ADMINISTRATOR_PERMISSION_LEVEL;
  const shownAvatarUrl = (secondaryUserModeIsActive && managedShopUserAvatarUrl) || avatarUrl || DEFAULT_AVATAR;

  const menuSections = [
    buildSellingSection(shopSlug, isShopAdmin),
    buildBuyingSection(user),
    isShopAdmin && shopPaymentMethod && buildFinancesSection(shopPaymentMethod),
    !loggedOut && buildMyAccountSection({ locale, includeMyProfileLink: false }),
  ].filter(Boolean);

  const themeExpEnabled = useExpEnabled(experiments.WEB_THEME_CONTROL);

  const menu = (
    <div className="reverb-header__menu__user-menu">
      <div className="bdb-1 bd-primary padding-2 pt-0">
        <div className="text-color-primary text-title-600 text-truncate-1">
          {name}
        </div>

        <CoreLink to={componentUrlHelpers.accountEditPath}>
          <RCText size="350" htmlTag="span">
            {I18n.t('discovery.reverbHeader.userMenu.editProfile')}
          </RCText>
        </CoreLink>
      </div>

      {themeExpEnabled &&
        <div className="bdb-1 bd-primary">
          <div className="reverb-header__menu__user-menu__header">
            <span className="ph ph-r ph-lock">{I18n.t('discovery.reverbHeader.userMenu.theme')}</span>
          </div>
          <WebThemeControl />
        </div>
      }

      {!!managedShopName && renderSecondaryUserMenu(secondaryUserModeIsActive, managedShopName)}

      <ReverbHeaderCreditCallouts
        walletBalance={walletBalance}
        reverbCreditsBalances={reverbCredits}
        payoutBalances={payoutBalances}
        paymentMethod={shopPaymentMethod}
      />

      {menuSections.map(({ key, title, links, icon }) => (
        <ReverbHeaderMenuSection key={key} title={title} links={links} icon={icon} />
      ))}
    </div>
  );

  const trigger = (
    <div
      className="reverb-header__nav__link reverb-header__nav__link--user-menu"
    >
      <div className="reverb-header__nav__link__icon reverb-header__nav__link--avatar">
        <img src={shownAvatarUrl} alt={!hideMenuLabel ? '' : I18n.t('discovery.reverbHeader.userMenu.badge')} />
      </div>
      {!hideMenuLabel &&
        <div className="reverb-header__nav__link__label">
          {I18n.t('discovery.reverbHeader.userMenu.badge')}
        </div>
      }
    </div>
  );

  return (
    <HeaderDropdown
      trigger={trigger}
      contents={menu}
      dropdownKey="user_menu"
      onOpen={key => setActiveDropdownKey(key)}
      activeDropdownKey={activeDropdownKey}
    />
  );
}

export default ReverbHeaderDesktopMenu;
